import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ComplianceReportListResponse,
  ComplianceReportListParams,
  ComplianceReportDetailDto,
  ComplianceReportDto,
  GenerateReportResult,
  AmendReportRequest,
  AmendReportResult,
  GenerateReportRequest,
  FilePackageRequest,
  ReportPreviewResponse,
  RecordReceiptRequest,
  DismissReceiptChaseRequest,
  ReportBeginningBalanceRequest,
  ReportFinalReportReasonRequest,
  AmendmentModel,
  FilingPreviewDto,
  PreflightResult,
  SigningEvidenceDto,
  FilingChainDto,
  EffectivePreviewDto,
  WarningDispositionInput,
  PeriodLockMutationRequest,
  PeriodLockMutationResult,
  MarkExportBatchUploadedRequest,
  MarkExportBatchUploadedResultDto,
  TransactionResendKind,
  TransactionResendRequest,
  TransactionResendResultDto,
  MarkFiledInPortalRequest,
  MarkFiledInPortalResultDto,
  MarkFiledPreviewDto,
  ReportReopenResult,
  ReopenAdvisory,
  PreSessionReportPreview,
  PreSessionSessionDateConfirmResult,
  RapidReportMarkReportedResponse,
  DownstreamCashRecalcPreview,
} from '@signalvote/shared';
import { ErrorCode } from '@signalvote/shared';
import { authService } from '../../services/authService';
import { API_BASE_URL } from '../../config';
import { apiJson, apiResponse, type ApiErrorBody } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';
import {
  optimisticallyPatchReportDetail,
  reconcileReportDetailVersion,
  reportRowScope,
  rollbackReportDetail,
} from './reportDetailOptimistic';
import { useCampaignStore } from '../../stores';
import { FINANCE_STALE_TIME } from './queryUtils';

/**
 * The error most report mutations throw on a non-ok response: the server's sentence (`error` on a
 * refusal, `message` on a body with no code), else the fallback — never the `code`. `??`, not `||`:
 * an empty sentence is kept. `body` is null when the response carried a JSON `null`.
 */
function sentenceOr(fallback: string) {
  return (body: ApiErrorBody | null) => new Error(body?.error ?? body?.message ?? fallback);
}

// Re-export types for consumer convenience
export type {
  ComplianceReportListResponse,
  ComplianceReportListParams,
  ComplianceReportDetailDto,
  GenerateReportResult,
  ReportPreviewResponse,
};

// ============================================================================
// Cross-period cash recalc preview (issue #643)
// ============================================================================

/**
 * Warn-before-confirm data for the cross-period cash recalc: given the date a
 * cash-affecting edit would land, which later EDITABLE periods would have their
 * opening balance re-seeded, and which later FILED periods would be left
 * inconsistent (a portal-amendment matter). Read-only — the recalc itself runs
 * synchronously as part of the confirmed mutation. Pass `null` to disable.
 */
export function useCashRecalcPreview(date: string | null) {
  const selectedCampaign = useCampaignStore((s) => s.selectedCampaign);
  // The preview requires Finance Do; below that the server only answers 403.
  const canDoFinance = useCampaignStore((s) => s.canDo('Finance'));

  return useQuery({
    queryKey: queryKeys.finance.reports.cashRecalcPreview(date ?? ''),
    queryFn: () =>
      apiJson<DownstreamCashRecalcPreview>(`/api/finance/reports/cash-recalc-preview?date=${date}`, {
        error: 'Failed to fetch cash recalc preview',
      }),
    enabled: authService.isAuthenticated() && !!selectedCampaign && !!date && canDoFinance,
    // The named later periods must stay fresh as earlier-period edits land.
    staleTime: 0,
  });
}

// ============================================================================
// Report Preview (FIP-008)
// ============================================================================

export function useReportPreview(deadlineId: string | null) {
  const selectedCampaign = useCampaignStore((s) => s.selectedCampaign);

  return useQuery({
    queryKey: queryKeys.finance.reports.preview(deadlineId ?? ''),
    queryFn: async () => {
      const response = await apiResponse(`/api/finance/reports/preview?deadlineId=${deadlineId}`);
      if (response.status === 404) return null;
      if (!response.ok) throw new Error('Failed to fetch report preview');
      return response.json() as Promise<ReportPreviewResponse>;
    },
    enabled: authService.isAuthenticated() && !!selectedCampaign && !!deadlineId,
    staleTime: 30_000,
  });
}

// ============================================================================
// Report List
// ============================================================================

function buildReportListPath(params: ComplianceReportListParams): string {
  const searchParams = new URLSearchParams();
  if (params.page != null) searchParams.set('page', String(params.page));
  if (params.pageSize != null) searchParams.set('pageSize', String(params.pageSize));
  if (params.status) searchParams.set('status', params.status);
  if (params.deadlineId) searchParams.set('deadlineId', params.deadlineId);
  return `/api/finance/reports?${searchParams.toString()}`;
}

export function useFinanceReports(params: ComplianceReportListParams) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.reports.list(params),
    queryFn: () =>
      apiJson<ComplianceReportListResponse>(buildReportListPath(params), {
        error: 'Failed to fetch reports',
      }),
    enabled: authService.isAuthenticated() && !!selectedCampaign,
    staleTime: FINANCE_STALE_TIME,
  });
}

// ============================================================================
// Report Detail
// ============================================================================

export function useFinanceReportDetail(id: string | null) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.reports.detail(id ?? ''),
    queryFn: async () => {
      const response = await apiResponse(`/api/finance/reports/${id}`);
      if (response.status === 404) return null;
      if (!response.ok) throw new Error('Failed to fetch report detail');
      return response.json() as Promise<ComplianceReportDetailDto>;
    },
    enabled: authService.isAuthenticated() && !!selectedCampaign && !!id,
  });
}

// ============================================================================
// Generate Report
// ============================================================================

export function useGenerateReport() {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationFn: async (request: GenerateReportRequest) => {
      const response = await apiResponse('/api/finance/reports/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        json: request,
      });
      if (response.status === 404) throw new Error('Deadline not found');
      if (response.status === 409) {
        // 409 now spans two guards: the duplicate-report guard and the ADR-064 sequential
        // file-order guard. The latter's message names the earlier period to close first, so
        // surface it verbatim; otherwise keep the duplicate copy. (Guarded read so a body-less
        // 409 still throws the duplicate message.)
        const err = response.json ? await response.json().catch(() => null) : null;
        if (err?.code === ErrorCode.PriorPeriodNotClosed) {
          throw new Error(err.error ?? 'Close the earlier reporting period first, then generate this one.');
        }
        throw new Error('Report already exists for this deadline');
      }
      if (!response.ok) throw new Error('Failed to generate report');
      return response.json() as Promise<GenerateReportResult>;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.deadlines.all });
    },
  });

  return { generateReport: mutateAsync };
}

// ============================================================================
// Generate Whole Report (#126) — the non-finalizing whole-document generate
// seam for a WholeReport + uploadIsFiling=false jurisdiction (VA). POST
// /reports/{id}/generate builds the report's SubmissionPackage and streams the
// XML for upload to the state portal WITHOUT finalizing: no Confirmed→Reported
// flip, no Status=Filed, no cash freeze (generate ≠ file, ADR-019). Repeatable
// and free. The treasurer uploads to COMET, then finalizes via
// `mark-filed-in-portal`. Distinct from `useGenerateReport` (which CREATES a
// report from a deadline) and from `useExportDelta` (AL incremental delta).
// ============================================================================

export interface GenerateWholeReportResult {
  fileBlob: Blob;
  fileName: string;
  submissionPackageId: string | null;
}

/**
 * #313 — a typed whole-report-generate failure carrying the server's stable `code` so the
 * generate button can branch on it (e.g. the missing-State-Filer-ID config error → a Finance
 * Settings link) instead of string-matching the human message. Mirrors `MarkFiledInPortalError` /
 * `ObligationGenerateError`. `code` is null when the body carried no structured code (a non-JSON
 * plain-string body — see `readGenerateWholeReportError`).
 */
export class GenerateWholeReportError extends Error {
  constructor(public readonly code: string | null, message: string) {
    super(message);
    this.name = 'GenerateWholeReportError';
  }
}

/**
 * #313 — parse a non-ok generate response into a typed error WITHOUT losing the specific message.
 * The endpoint returns a structured JSON 400 (`{ error, code }`, the sentence in `error`) for its named
 * refusals (the NC missing-filer-id case is `MapSubmissionPackageException` →
 * `{ code: 'MissingStateFilerId', … }`), so the JSON path preserves the stable code + sentence. But a body that is NOT JSON must
 * still surface verbatim rather than collapse to the generic fallback (the epic's live-path rule),
 * so we read the body as text once and fall back to the raw string when it doesn't parse.
 */
async function readGenerateWholeReportError(response: Response): Promise<GenerateWholeReportError> {
  const raw = await response.text().catch(() => '');
  let code: string | null = null;
  let message: string | null = null;
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === 'object') {
        const body = parsed as { error?: unknown; code?: unknown; message?: unknown };
        code = typeof body.code === 'string' ? body.code : null;
        message =
          typeof body.error === 'string'
            ? body.error
            : typeof body.message === 'string'
              ? body.message
              : null;
      } else if (typeof parsed === 'string') {
        // A JSON-encoded bare string body (some controllers return BadRequest(string)).
        message = parsed;
      }
    } catch {
      // Non-JSON plain-string body — keep it verbatim so the specific message survives.
      message = raw;
    }
  }
  return new GenerateWholeReportError(code, message ?? 'Failed to generate the report');
}

export function useGenerateWholeReport() {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationFn: async (reportId: string): Promise<GenerateWholeReportResult & { reportId: string }> => {
      const response = await authService.authenticatedFetch(
        `${API_BASE_URL}/api/finance/reports/${reportId}/generate`,
        { method: 'POST' },
      );
      if (!response.ok) {
        throw await readGenerateWholeReportError(response);
      }
      const fileBlob = await response.blob();
      // Jurisdiction-neutral fallback (matches the sibling download hooks). The real
      // filename comes from Content-Disposition; this only fires if the server omits it.
      const fallback = `${reportId}-report.xml`;
      const fileName = readDispositionFileName(response.headers.get('Content-Disposition'), fallback);
      return {
        fileBlob,
        fileName,
        submissionPackageId: response.headers.get('X-Submission-Package-Id'),
        reportId,
      };
    },
    onSuccess: (result) => {
      // Generate is non-finalizing but persists/refreshes the report's SubmissionPackage;
      // refresh the detail + list so any package-driven surface reflects the latest build.
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.detail(result.reportId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.all });
    },
  });

  return { generateWholeReport: mutateAsync };
}

// ============================================================================
// File Report (FIN-010) — endpoint returns an application/zip body plus
// X-Amendment-Model and X-Submission-Package-Id headers. Callers receive a
// structured result so the UI can both trigger a browser download and surface
// the amendment hint. ADR-021 — the header carries the ONE ruleset-declared
// amendment axis (federal = ElectronicResubmit, distinct from LA/VA's
// SelfContainedUpload), replacing the retired X-Amendment-Workflow header.
// ============================================================================

export interface FileReportResult {
  zipBlob: Blob;
  fileName: string;
  amendmentModel: AmendmentModel | null;
  submissionPackageId: string | null;
}

function readDispositionFileName(disposition: string | null, fallback: string): string {
  if (!disposition) return fallback;
  // Match RFC 5987 filename* first (UTF-8 percent-encoded), fall back to plain filename=.
  const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (star?.[1]) return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''));
  const plain = /filename=("?)([^";]+)\1/i.exec(disposition);
  if (plain?.[2]) return plain[2].trim();
  return fallback;
}

export function useFileReport() {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationFn: async ({
      reportId,
      data,
    }: { reportId: string; data: FilePackageRequest }): Promise<
      FileReportResult & { reportId: string; concurrencyVersion: number | null }
    > => {
      const response = await authService.authenticatedFetch(
        `${API_BASE_URL}/api/finance/reports/${reportId}/file`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        },
      );
      if (!response.ok) {
        // Service-side errors carry { error, code, payload? } — surface the
        // human sentence (`error`) when present so the UI can render an actionable toast.
        const err = await response.json().catch(() => null);
        // PPR-009: the unreconciled-contributions guard returns a structured
        // payload the modal must render — typed exception so the catch block
        // can switch on error code without parsing.
        if (err?.code === ErrorCode.UnconfirmedContributionsInPeriod) {
          throw new FileReportUnconfirmedContributionsError(
            err.error ?? 'Unreconciled contributions blocking this filing.',
            (err.payload?.contributionIds as string[] | undefined) ?? [],
            (err.payload?.count as number | undefined) ?? 0,
          );
        }
        // FPD-002: attestation-shape failures keep the modal open with the
        // server message; other errors close it and bubble through.
        if (err?.code === ErrorCode.AttestationMissing
            || err?.code === ErrorCode.AttestationNameMismatch
            || err?.code === ErrorCode.AttestationReasonMissing) {
          throw new FileReportAttestationError(
            err.code,
            err.error ?? 'Attestation validation failed. Re-confirm and retry.',
          );
        }
        // §25 — no in-system package to generate (transactions already uploaded via
        // Export / period filed in the portal). Typed so the modal can soften it.
        if (err?.code === ErrorCode.ExternalPortalReport) {
          throw new FileReportPortalNoPackageError(
            err.error ?? 'This period has no in-system package to generate.',
          );
        }
        throw new Error(err?.error ?? err?.message ?? 'Failed to file report');
      }
      const zipBlob = await response.blob();
      const fallback = `report-${reportId}.zip`;
      const fileName = readDispositionFileName(response.headers.get('Content-Disposition'), fallback);
      const modelHeader = response.headers.get('X-Amendment-Model') as AmendmentModel | null;
      // The body is the zip, so the report's new token rides a header.
      const versionHeader = response.headers.get('X-Report-Concurrency-Version');
      return {
        zipBlob,
        fileName,
        amendmentModel: modelHeader,
        submissionPackageId: response.headers.get('X-Submission-Package-Id'),
        reportId,
        concurrencyVersion: versionHeader === null ? null : Number(versionHeader),
      };
    },
    onSuccess: (result) => {
      if (result.concurrencyVersion !== null) {
        reconcileReportDetailVersion(queryClient, result.reportId, result.concurrencyVersion);
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.all });
      // The report page is open while filing — flip Ready → Filed without
      // a manual reload by invalidating the by-id detail query too.
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.detail(result.reportId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.health() });
      // FIP-008: Filing moves transactions to Reported and clears obligations
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.obligations() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.deadlines.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.bankTransactions.all });
    },
  });

  return { fileReport: mutateAsync };
}

// ============================================================================
// Download Package (FIN-011) — GET /reports/{id}/package returns the same
// shape of zip as POST /file. Used by the "Download Package" button on
// filed-report cards.
// ============================================================================

export function useDownloadReportPackage() {
  const { mutateAsync } = useMutation({
    mutationFn: async (reportId: string): Promise<FileReportResult> => {
      const response = await authService.authenticatedFetch(
        `${API_BASE_URL}/api/finance/reports/${reportId}/package`,
      );
      if (!response.ok) {
        const err = await response.json().catch(() => null);
        throw new Error(err?.error ?? 'Failed to download package');
      }
      const zipBlob = await response.blob();
      const fallback = `report-${reportId}.zip`;
      const fileName = readDispositionFileName(response.headers.get('Content-Disposition'), fallback);
      return {
        zipBlob,
        fileName,
        amendmentModel: null,
        submissionPackageId: null,
      };
    },
  });

  return { downloadPackage: mutateAsync };
}

// ============================================================================
// Download CRO-1000 cover (ADR-054) — GET /reports/{id}/cover returns the
// signable CRO-1000 cover PDF as a distinct affordance from the package zip.
// North Carolina only (404 elsewhere); surfaced on the post-file panel so the
// treasurer can get the cover, sign it, and send it.
// ============================================================================

export interface CoverDownloadResult {
  blob: Blob;
  fileName: string;
}

export function useDownloadReportCover() {
  const { mutateAsync } = useMutation({
    mutationFn: async (reportId: string): Promise<CoverDownloadResult> => {
      const response = await authService.authenticatedFetch(
        `${API_BASE_URL}/api/finance/reports/${reportId}/cover`,
      );
      if (!response.ok) {
        const err = await response.json().catch(() => null);
        throw new Error(err?.error ?? 'Failed to download cover');
      }
      const blob = await response.blob();
      const fallback = `cro-1000-cover-${reportId}.pdf`;
      const fileName = readDispositionFileName(response.headers.get('Content-Disposition'), fallback);
      return { blob, fileName };
    },
  });

  return { downloadCover: mutateAsync };
}

// ============================================================================
// Export new AL transactions (ADR-020) — POST /reports/{id}/export-delta builds
// the incremental EDI file of the period's NOT-yet-uploaded Confirmed rows. The
// AL FCPA portal is append-only, so each transaction uploads exactly once. The
// endpoint returns either an application/xml body (the delta file, with an
// X-Export-Batch-Id header) or a 200 JSON `{ exported: false }` when there's
// nothing new to export.
// ============================================================================

export type ExportDeltaResult =
  | { exported: true; fileBlob: Blob; fileName: string; exportBatchId: string | null }
  | { exported: false; message: string };

export function useExportDelta() {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationFn: async (reportId: string): Promise<ExportDeltaResult & { reportId: string }> => {
      const response = await authService.authenticatedFetch(
        `${API_BASE_URL}/api/finance/reports/${reportId}/export-delta`,
        { method: 'POST' },
      );
      if (!response.ok) {
        const err = await response.json().catch(() => null);
        throw new Error(err?.error ?? err?.message ?? 'Failed to export new transactions');
      }
      // An empty delta returns a JSON no-op; a real delta returns the EDI file.
      const contentType = response.headers.get('Content-Type') ?? '';
      if (contentType.includes('application/json')) {
        const body = (await response.json().catch(() => null)) as
          | { exported?: boolean; message?: string }
          | null;
        return {
          exported: false,
          message: body?.message ?? 'There are no new transactions to export.',
          reportId,
        };
      }
      const fileBlob = await response.blob();
      // Jurisdiction-neutral fallback (matches the sibling download hooks). The real
      // filename comes from Content-Disposition; this only fires if the server omits it.
      const fallback = `${reportId}-new-transactions.xml`;
      const fileName = readDispositionFileName(response.headers.get('Content-Disposition'), fallback);
      return {
        exported: true,
        fileBlob,
        fileName,
        exportBatchId: response.headers.get('X-Export-Batch-Id'),
        reportId,
      };
    },
    onSuccess: (result) => {
      // A produced draft changes the report's not-yet-uploaded set; refresh the
      // detail + list so the on-screen delta matches what was just exported.
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.detail(result.reportId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.all });
    },
  });

  return { exportDelta: mutateAsync };
}

// ============================================================================
// Request a single-transaction re-send (#645/#647) — POST
// /api/finance/transaction-resends records a deliberate, audited re-send of ONE already-sent
// transaction the state lost (or that the treasurer deleted in the portal). The AL FCPA portal is
// append-only (ADR-020), so the automatic export never re-emits a sent row; this is the one
// sanctioned exception. The next export force-includes exactly that transaction once. Invalidates the
// report detail so the row's server-computed `resendQueued` flag refreshes to true (the action then
// reads "re-send queued" and disables — re-sends can't be piled up).
// ============================================================================

export function useRequestTransactionResend() {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationFn: async ({
      reportId: _reportId,
      kind,
      transactionId,
    }: {
      // Carried only so onSuccess can invalidate the right report-detail cache — the API body is just
      // { kind, transactionId } (the re-send is keyed on the transaction, not the report).
      reportId: string;
      kind: TransactionResendKind;
      transactionId: string;
    }): Promise<TransactionResendResultDto> => {
      const request: TransactionResendRequest = { kind, transactionId };
      return apiJson<TransactionResendResultDto>('/api/finance/transaction-resends', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        json: request,
        error: sentenceOr('Failed to queue the re-send'),
      });
    },
    onSuccess: (_result, variables) => {
      // The row's `resendQueued` flips server-side; refresh the detail (+ list) so the screen shows
      // the queued state and disables the action.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.finance.reports.detail(variables.reportId),
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.all });
    },
  });

  return { requestResend: mutateAsync };
}

// ============================================================================
// Mark export uploaded (ADR-020) — answer the §4.1 acceptance gate. POST
// /export-batches/{id}/mark-uploaded with `{ accepted }`. "Accepted" asks whether
// the AL FCPA portal IMPORTED the file (not whether you clicked upload) — the portal
// is the only validator, so a yes-to-a-rejected-file would silently drop the rows.
// Yes flips the draft's member rows Confirmed → Reported; No discards the draft and
// returns the rows to the not-yet-uploaded pool. A stale answer (the draft is no
// longer Generated) is a 409.
// ============================================================================

export function useMarkExportBatchUploaded() {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationFn: async ({
      exportBatchId,
      accepted,
    }: {
      exportBatchId: string;
      accepted: boolean;
      // reportId rides along only so onSuccess can target the detail query — the
      // banner lives on the report-detail read.
      reportId: string;
    }): Promise<MarkExportBatchUploadedResultDto> => {
      return apiJson<MarkExportBatchUploadedResultDto>(
        `/api/finance/export-batches/${exportBatchId}/mark-uploaded`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          json: { accepted } satisfies MarkExportBatchUploadedRequest,
          error: sentenceOr('Failed to confirm the export upload'),
        },
      );
    },
    onSuccess: (_result, { reportId }) => {
      // The Confirmed → Reported flip changes the report's Reported set AND clears the
      // derived banner; refresh the surfaces the flip touches — detail (banner + rows),
      // list, the bank-line lock state, and the health-bar counts
      // (mirrors useFileReport, which also flips rows to Reported).
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.detail(reportId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.bankTransactions.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.health() });
      // The close-period ledger reads the sort-inbox display query; the row's
      // status badge follows the Reported flip, so invalidate that key too or the
      // ledger keeps showing "not reported" until it ages out on a later remount.
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.sortInboxAll() });
    },
  });

  return { markExportBatchUploaded: mutateAsync };
}

// ============================================================================
// Mark filed in portal (ADR-023) — POST /reports/{id}/mark-filed-in-portal. The
// treasurer attests the PERIOD REPORT was filed in the portal (upload ≠ file). This is
// now a COMMITTING attestation: it sets Status = Filed + FilingOrigin = PortalAttested
// AND flips the report's period `Confirmed` rows `Confirmed → Reported` in the same
// SaveChanges. The confirmation number is optional; a stale concurrencyVersion is a 409.
// New 409 codes: `MarkFiledPendingExportMustResolveFirst` (a live Generated export draft
// must be resolved first) and `MarkFiledAlreadyFiled` (should be gated away — Reopen is
// the only forward move once filed). Both surface as a typed `MarkFiledInPortalError`.
// ============================================================================

/**
 * ADR-023 — a typed mark-filed failure carrying the server's stable `code` so the
 * button can map it to specific treasurer copy (the pending-export block, the
 * already-filed edge) instead of parsing the message.
 */
export class MarkFiledInPortalError extends Error {
  constructor(public readonly code: string, message: string) {
    super(message);
    this.name = 'MarkFiledInPortalError';
  }
}

export function useMarkFiledInPortal(reportId?: string) {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    // Serialize this report row's actions (confirm → record receipt → …); other rows stay parallel.
    scope: reportId ? reportRowScope(reportId) : undefined,
    mutationFn: async ({
      reportId,
      data,
    }: {
      reportId: string;
      data: MarkFiledInPortalRequest;
    }): Promise<MarkFiledInPortalResultDto> => {
      const response = await apiResponse(`/api/finance/reports/${reportId}/mark-filed-in-portal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        json: data,
      });
      if (response.status === 404) throw new Error('Report not found');
      if (!response.ok) {
        const err = await response.json().catch(() => null);
        // Preserve the stable error code so the button can render the code-specific
        // copy (pending-export must-resolve, already-filed). Falls back to a plain
        // Error only when the server sent no code.
        if (err?.code) {
          throw new MarkFiledInPortalError(err.code as string, err.error ?? 'Failed to mark the report filed');
        }
        throw new Error(err?.error ?? err?.message ?? 'Failed to mark the report filed');
      }
      return response.json() as Promise<MarkFiledInPortalResultDto>;
    },
    // Optimistic-token recipe (apps/web/CLAUDE.md). The server computes everything confirm-filed
    // changes, so onMutate only snapshots; onSuccess writes the returned token back so the follow-up
    // receipt edit on this row doesn't false-conflict.
    onMutate: ({ reportId }) => optimisticallyPatchReportDetail(queryClient, reportId),
    onError: (_err, { reportId }, snapshot) => rollbackReportDetail(queryClient, reportId, snapshot),
    onSuccess: (result, { reportId }) =>
      reconcileReportDetailVersion(queryClient, reportId, result.report.concurrencyVersion),
    onSettled: (_result, _err, { reportId }) => {
      // Status = Filed + rows flipped to Reported: refresh detail (status/cash/origin/
      // canReopen), the list, the deadline/obligation views, and the bank surface
      // whose lock state follows the Reported flip (mirrors useMarkExportBatchUploaded).
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.detail(reportId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.deadlines.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.obligations() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.health() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.bankTransactions.all });
      // The close-period ledger reads the sort-inbox display query; the rows
      // flipped Confirmed → Reported here must refresh there too, or the ledger
      // keeps showing "not reported" until it ages out on a later remount.
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.sortInboxAll() });
    },
  });

  return { markFiledInPortal: mutateAsync };
}

// ============================================================================
// Mark-filed preview (ADR-023) — GET /reports/{id}/mark-filed-preview (View perm). The
// pre-confirm count the always-confirm dialog renders: how many `Confirmed` rows the
// attestation will commit to `Reported`. Fetched only while the mark-filed action is on
// screen (the button self-gates); returns null when the report is out of the campaign.
// ============================================================================

export function useMarkFiledPreview(reportId: string | null) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.reports.markFiledPreview(reportId ?? ''),
    queryFn: async () => {
      const response = await apiResponse(`/api/finance/reports/${reportId}/mark-filed-preview`);
      if (response.status === 404) return null;
      if (!response.ok) throw new Error('Failed to fetch the mark-filed preview');
      return response.json() as Promise<MarkFiledPreviewDto>;
    },
    enabled: authService.isAuthenticated() && !!selectedCampaign && !!reportId,
    // The count must match the report's live Confirmed set at confirm time.
    staleTime: 0,
  });
}

// ============================================================================
// Reopen report (ADR-023) — POST /reports/{id}/reopen (Manage perm). The symmetric undo
// of "mark filed in portal": reverts Status → Ready, clears the frozen as-filed balances
// + FilingOrigin + deadline lock, and reverts the rows the attestation committed
// (Reported → Confirmed), leaving genuinely-uploaded ExportBatch members Reported. Body
// echoes `report.concurrencyVersion` (never hardcode 0). Only `filedWithState: false`
// reopens. Errors: ReopenReportNotFound (404), ReopenReportNotReopenable (400),
// ReopenReportFiledWithStateUseUnlock (400, a yes or no answer: use the period unlock),
// ReopenReportConcurrencyMismatch (409).
// ============================================================================

// ADR-064 — the amendment-first / attest-then-reopen advisory for a Closed period. Read-only
// guidance the "change this closed period" affordance fetches to decide whether to lead with
// amendment (a filing is on record) or reopen (an in-app-only no-activity close — a clean undo),
// and to surface the later-Closed-period warning before the treasurer commits. staleTime=0 so the
// decision is fresh each open; `enabled` lets the affordance defer the fetch until it's needed.
export function useReopenAdvisory(reportId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.finance.reports.reopenAdvisory(reportId),
    queryFn: (): Promise<ReopenAdvisory> =>
      apiJson<ReopenAdvisory>(`/api/finance/reports/${reportId}/reopen-advisory`, {
        error: 'Failed to load reopen advisory',
      }),
    enabled: enabled && !!reportId,
    staleTime: 0,
  });
}

export function useReopenReport(reportId?: string) {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    // Serialize this report row's actions (confirm → record receipt → …); other rows stay parallel.
    scope: reportId ? reportRowScope(reportId) : undefined,
    mutationFn: async ({
      reportId,
      concurrencyVersion,
      filedWithState,
    }: {
      reportId: string;
      concurrencyVersion: number;
      // ADR-064 attest-then-reopen: the treasurer's "have you actually filed?" answer. Omitted =
      // not answered; false = clean reopen; true = still reopens (alert-don't-block) but warns.
      filedWithState?: boolean | null;
    }): Promise<ReportReopenResult> => {
      const response = await apiResponse(`/api/finance/reports/${reportId}/reopen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        json: { concurrencyVersion, filedWithState },
      });
      if (response.status === 404) throw new Error('Report not found');
      if (!response.ok) {
        const err = await response.json().catch(() => null);
        if (err?.code === ErrorCode.ReopenReportConcurrencyMismatch || response.status === 409) {
          throw new Error('This report changed since you loaded it. Refresh and try again.');
        }
        throw new Error(err?.error ?? err?.message ?? 'Failed to reopen the report');
      }
      return response.json() as Promise<ReportReopenResult>;
    },
    // Optimistic-token recipe (apps/web/CLAUDE.md): snapshot on mutate, roll back on error, write the
    // returned token back on success, invalidate on settle.
    onMutate: ({ reportId }) => optimisticallyPatchReportDetail(queryClient, reportId),
    onError: (_err, { reportId }, snapshot) => rollbackReportDetail(queryClient, reportId, snapshot),
    onSuccess: (result, { reportId }) =>
      reconcileReportDetailVersion(queryClient, reportId, result.report.concurrencyVersion),
    onSettled: (_result, _err, { reportId }) => {
      // Status → Ready + rows reverted to unreported: refresh the detail (status/cash/
      // canReopen), the list, and the deadline/obligation + bank surfaces the
      // revert touches, so the UI returns to the editable Ready state without a reload.
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.detail(reportId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.reopenAdvisory(reportId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.deadlines.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.obligations() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.health() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.bankTransactions.all });
      // The close-period ledger reads the sort-inbox query, and the reopen changes BOTH facts
      // it renders: the rows revert to unreported, and on the clean path the period unlocks,
      // which is what makes them editable. That query has a 60s staleTime and doesn't refetch
      // on focus, so without this the rows stay read-only for up to a minute after a reopen
      // that already worked.
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.sortInboxAll() });
    },
  });

  return { reopenReport: mutateAsync };
}

// ============================================================================
// FPD-026 — Download Audit Packet. Sibling to useDownloadReportPackage but
// targets the FPD-014 audit-packet ZIP endpoint and surfaces typed HTTP
// status discriminators so the consumer can render the AC's literal copy
// per error class without parsing the body.
// ============================================================================

export type AuditPacketDownloadStatusKind = 'Forbidden' | 'NotFound' | 'ServerError';

export class AuditPacketDownloadError extends Error {
  constructor(public readonly kind: AuditPacketDownloadStatusKind) {
    super(`AuditPacketDownload:${kind}`);
    this.name = 'AuditPacketDownloadError';
  }
}

export interface AuditPacketDownloadResult {
  zipBlob: Blob;
  fileName: string;
}

export function useDownloadAuditPacket() {
  const { mutateAsync } = useMutation({
    mutationFn: async (reportId: string): Promise<AuditPacketDownloadResult> => {
      const response = await authService.authenticatedFetch(
        `${API_BASE_URL}/api/finance/reports/${reportId}/audit-packet.zip`,
      );
      if (!response.ok) {
        // Per the AC, 403 / 404 / 5xx each map to a distinct toast literal.
        // The hook surfaces the kind; the component owns copy lookup so we
        // don't bake strings into the data layer.
        if (response.status === 403) throw new AuditPacketDownloadError('Forbidden');
        if (response.status === 404) throw new AuditPacketDownloadError('NotFound');
        throw new AuditPacketDownloadError('ServerError');
      }
      const zipBlob = await response.blob();
      // FPD-026 ticket Technical Notes: filename comes from the
      // Content-Disposition header set server-side; the frontend doesn't
      // compute it. Fallback is a regression guard.
      const fallback = `audit-packet-${reportId}.zip`;
      const fileName = readDispositionFileName(response.headers.get('Content-Disposition'), fallback);
      return { zipBlob, fileName };
    },
  });

  return { downloadAuditPacket: mutateAsync };
}

// ============================================================================
// Record Receipt (FIN-011) — PATCH /reports/{id}/receipt for treasurer-entered
// state filing receipt.
// ============================================================================

export function useRecordReceipt(reportId?: string) {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    // Serialize this report row's actions (confirm → record receipt → …); other rows stay parallel.
    scope: reportId ? reportRowScope(reportId) : undefined,
    mutationFn: async ({
      reportId,
      data,
    }: { reportId: string; data: RecordReceiptRequest }) => {
      const response = await apiResponse(`/api/finance/reports/${reportId}/receipt`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        json: data,
      });
      if (response.status === 404) throw new Error('Report not found');
      if (response.status === 409) {
        throw new Error('This report changed since you loaded it. Refresh and try again.');
      }
      if (!response.ok) {
        const err = await response.json().catch(() => null);
        throw new Error(err?.error ?? err?.message ?? 'Failed to record receipt');
      }
      return response.json() as Promise<ComplianceReportDto & { id: string }>;
    },
    // Optimistic-token recipe (apps/web/CLAUDE.md): the receipt fields are known client-side, so
    // they're patched on mutate (clearing = both empty → null).
    onMutate: ({ reportId, data }) =>
      optimisticallyPatchReportDetail(queryClient, reportId, {
        stateFilingReceiptId: data.stateFilingReceiptId || null,
        stateFilingReceiptAt: data.stateFilingReceiptAt,
      }),
    onError: (_err, { reportId }, snapshot) => rollbackReportDetail(queryClient, reportId, snapshot),
    onSuccess: (result, { reportId }) =>
      reconcileReportDetailVersion(queryClient, reportId, result.concurrencyVersion),
    onSettled: (_result, _err, { reportId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.all });
      // The close hub's receipt-chase card reads finance health.
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.health() });
      // Detail panel reads the receipt fields + the receipt-chase flag — refetch the by-id view so
      // the server-derived values render without a manual reload.
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.detail(reportId) });
    },
  });

  return { recordReceipt: mutateAsync };
}

// ============================================================================
// Dismiss the receipt chase — POST /reports/{id}/receipt-chase/dismiss. The treasurer's recorded
// decision to stop waiting for the receiving office's confirmation of receipt (proofOfFiling
// jurisdictions — MS). Same optimistic-token recipe as the receipt edit; the open-item flag itself is
// server-derived from the recorded decision, so it's patched closed here and reconciled on settle.
// ============================================================================

export function useDismissReceiptChase(reportId?: string) {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    scope: reportId ? reportRowScope(reportId) : undefined,
    mutationFn: async ({
      reportId: id,
      concurrencyVersion,
    }: { reportId: string } & DismissReceiptChaseRequest) => {
      const response = await apiResponse(`/api/finance/reports/${id}/receipt-chase/dismiss`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        json: { concurrencyVersion },
      });
      if (response.status === 404) throw new Error('Report not found');
      if (response.status === 409) {
        throw new Error('This report changed since you loaded it. Refresh and try again.');
      }
      if (!response.ok) {
        const err = await response.json().catch(() => null);
        throw new Error(err?.error ?? err?.message ?? 'Failed to dismiss the receipt reminder');
      }
      return response.json() as Promise<ComplianceReportDto & { id: string }>;
    },
    onMutate: ({ reportId: id }) =>
      optimisticallyPatchReportDetail(queryClient, id, { receiptChaseOpen: false }),
    onError: (_err, { reportId: id }, snapshot) => rollbackReportDetail(queryClient, id, snapshot),
    onSuccess: (result, { reportId: id }) =>
      reconcileReportDetailVersion(queryClient, id, result.concurrencyVersion),
    onSettled: (_result, _err, { reportId: id }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.all });
      // The close hub's receipt-chase card reads finance health.
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.health() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.detail(id) });
    },
  });

  return { dismissReceiptChase: mutateAsync };
}

// ============================================================================
// Override Beginning Balance (FCG-009) — PATCH /reports/{id}/beginning-balance.
// Treasurer adjusts the defaulted beginning balance (sourced from the prior
// period's as-filed snapshot) before filing. concurrencyVersion is threaded
// from the report detail DTO; a stale version is a 409.
// ============================================================================

export function useOverrideBeginningBalance(reportId?: string) {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    // Serialize this report row's actions (confirm → record receipt → …); other rows stay parallel.
    scope: reportId ? reportRowScope(reportId) : undefined,
    mutationFn: async ({
      reportId,
      data,
    }: { reportId: string; data: ReportBeginningBalanceRequest }) => {
      const response = await apiResponse(`/api/finance/reports/${reportId}/beginning-balance`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        json: data,
      });
      if (response.status === 404) throw new Error('Report not found');
      if (response.status === 409) {
        throw new Error('This report changed since you loaded it. Refresh and try again.');
      }
      if (!response.ok) {
        const err = await response.json().catch(() => null);
        throw new Error(err?.error ?? err?.message ?? 'Failed to update beginning balance');
      }
      return response.json() as Promise<ComplianceReportDto & { id: string }>;
    },
    // Optimistic-token recipe (apps/web/CLAUDE.md). Only the opening balance is patched — the
    // server recomputes the ending cash, which the settle refetch brings back.
    onMutate: ({ reportId, data }) =>
      optimisticallyPatchReportDetail(queryClient, reportId, { beginningBalance: data.beginningBalance }),
    onError: (_err, { reportId }, snapshot) => rollbackReportDetail(queryClient, reportId, snapshot),
    onSuccess: (result, { reportId }) =>
      reconcileReportDetailVersion(queryClient, reportId, result.concurrencyVersion),
    onSettled: (_result, _err, { reportId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.detail(reportId) });
    },
  });

  return { overrideBeginningBalance: mutateAsync };
}

/**
 * GitHub #778 — mark a report the committee's FINAL report (or clear it). A non-null
 * `finalReportReason` marks it final (for LA, checks the matching CVR box); null clears it.
 * Mirrors {@link useOverrideBeginningBalance}: plain `mutateAsync` + refetch (this is a rare,
 * deliberate click, not a latency-sensitive one, so no optimistic cache surgery). The
 * `concurrencyVersion` in the body is the guard — a stale token is a 409. Reject a locked-period
 * write (400) or a stale token (409) with a treasurer message.
 */
export function useSetFinalReportReason() {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationFn: async ({
      reportId,
      data,
    }: { reportId: string; data: ReportFinalReportReasonRequest }) => {
      const response = await apiResponse(`/api/finance/reports/${reportId}/final-report-reason`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        json: data,
      });
      if (response.status === 404) throw new Error('Report not found');
      if (response.status === 409) {
        throw new Error('This report changed since you loaded it. Refresh and try again.');
      }
      if (!response.ok) {
        const err = await response.json().catch(() => null);
        throw new Error(err?.error ?? err?.message ?? 'Failed to update the final-report flag');
      }
      return response.json() as Promise<ComplianceReportDto & { id: string }>;
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.all });
      // The detail panel reads finalReportReason + concurrencyVersion — refetch the by-id view
      // so the new value (and bumped token) render without a reload.
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.detail(result.id) });
    },
  });

  return { setFinalReportReason: mutateAsync };
}

// ============================================================================
// Filing Preflight (PPR-009)
// GET /reports/{id}/preview returns the dataset the modal renders. The
// `error` on the parsed payload (when present) carries the
// UnconfirmedContributionsInPeriod stable code thrown by FileReportAsync,
// so the same hook can be reused for the post-confirm 400 fallback.
// ============================================================================

export class FileReportUnconfirmedContributionsError extends Error {
  readonly contributionIds: string[];
  readonly count: number;
  constructor(message: string, contributionIds: string[], count: number) {
    super(message);
    this.name = 'FileReportUnconfirmedContributionsError';
    this.contributionIds = contributionIds;
    this.count = count;
  }
}

/**
 * §25 — filing was refused because the report has no in-system package to
 * generate: its transactions were already uploaded via Export (PortalAttested)
 * or the period was filed directly in the portal (ExternalPortal). Server error
 * code <code>ExternalPortalReport</code>. Typed so the preflight modal can render
 * a soft, non-alarming explanation instead of the raw red service error when the
 * report is still Ready (the export-confirm dead-end).
 */
export class FileReportPortalNoPackageError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FileReportPortalNoPackageError';
  }
}

/**
 * FPD-002 — server-side attestation validation failed
 * (<code>AttestationMissing</code>, <code>AttestationNameMismatch</code>, or
 * <code>AttestationReasonMissing</code>). The attestation modal keeps itself
 * open and renders the message so the treasurer can retry without
 * re-running preflight.
 */
type AttestationErrorCode =
  | typeof ErrorCode.AttestationMissing
  | typeof ErrorCode.AttestationNameMismatch
  | typeof ErrorCode.AttestationReasonMissing;

export class FileReportAttestationError extends Error {
  readonly code: AttestationErrorCode;
  constructor(
    code: AttestationErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'FileReportAttestationError';
    this.code = code;
  }
}

/**
 * FPD-005 — alert-don't-block preflight payload feed for the Heads-up card.
 * Returns `null` when the report isn't in the current campaign.
 */
export function useFilingPreflight(reportId: string | null) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.reports.preflight(reportId ?? ''),
    queryFn: async () => {
      const response = await apiResponse(`/api/finance/reports/${reportId}/preflight`);
      if (response.status === 404) return null;
      if (!response.ok) throw new Error('Failed to fetch filing preflight');
      return response.json() as Promise<PreflightResult>;
    },
    enabled: authService.isAuthenticated() && !!selectedCampaign && !!reportId,
    // Heads-up card refreshes on every modal open — staleTime=0 keeps the
    // warning set in lockstep with workspace mutations.
    staleTime: 0,
  });
}

/**
 * FPD-013 — frozen signing-evidence read for the "Why is this here?" panel.
 * Returns null when the report has no SubmissionPackage (Ready report) or
 * the report isn't in the current campaign. The payload is frozen at file
 * time so a long staleTime is safe; the queryKey already isolates per
 * report id + campaign.
 */
export function useSigningEvidence(reportId: string | null) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.reports.signingEvidence(reportId ?? ''),
    queryFn: async () => {
      const response = await apiResponse(`/api/finance/reports/${reportId}/signing-evidence`);
      if (response.status === 404) return null;
      if (!response.ok) throw new Error('Failed to fetch signing evidence');
      return response.json() as Promise<SigningEvidenceDto>;
    },
    enabled: authService.isAuthenticated() && !!selectedCampaign && !!reportId,
    staleTime: 5 * 60_000,
  });
}

/**
 * FPD-024 — amendment-chain enumeration for the filing-version picker. The
 * picker mounts on the "Why is this here?" panel (FPD-013) and the
 * audit-packet download UI (FPD-026); both share this cache so switching
 * surfaces re-uses the same chain without a refetch. The chain changes only on
 * amend, generate and file, and each of those invalidates `reports.all`, so a
 * long staleTime is correct.
 */
export function useFilingChain(reportId: string | null) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.reports.filingChain(reportId ?? ''),
    queryFn: async () => {
      const response = await apiResponse(`/api/finance/reports/${reportId}/chain`);
      if (response.status === 404) return null;
      if (!response.ok) throw new Error('Failed to fetch filing chain');
      return response.json() as Promise<FilingChainDto>;
    },
    enabled: authService.isAuthenticated() && !!selectedCampaign && !!reportId,
    staleTime: 5 * 60_000,
  });
}

/**
 * FPD-020 — Confirm-filing modal's effective-count preview. Runs the same
 * inclusion gate the file-time builder will, so what the modal shows is what
 * the file actually contains. Returns the contribution / expenditure / refund
 * totals + `attestedPendingCount` + `excludedDueToStatusDrift`.
 *
 * Wired as a `useMutation` rather than `useQuery` because the in-flight
 * `WarningDispositions` change as the treasurer attests rows; each attest
 * round-trips for the latest count. Re-running this hook with stale dispositions
 * would falsify the modal totals.
 */
export function usePreviewWithDispositions() {
  const { mutateAsync } = useMutation({
    mutationFn: async (vars: {
      reportId: string;
      warningDispositions: WarningDispositionInput[];
    }) => {
      const response = await apiResponse(
        `/api/finance/reports/${vars.reportId}/preview-with-dispositions`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          json: { warningDispositions: vars.warningDispositions },
        },
      );
      if (response.status === 404) return null;
      if (!response.ok) throw new Error('Failed to preview filing with dispositions');
      return (await response.json()) as EffectivePreviewDto;
    },
  });
  return { previewWithDispositions: mutateAsync };
}

export function useFilingPreview(reportId: string | null) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.reports.filingPreview(reportId ?? ''),
    queryFn: async () => {
      const response = await apiResponse(`/api/finance/reports/${reportId}/preview`);
      if (response.status === 404) return null;
      if (!response.ok) throw new Error('Failed to fetch filing preview');
      return response.json() as Promise<FilingPreviewDto>;
    },
    enabled: authService.isAuthenticated() && !!selectedCampaign && !!reportId,
    // Modal pulls fresh data each open — staleTime=0 so closing and re-opening
    // in quick succession always refetches (workspace mutations between opens
    // may not have invalidated the cache yet on slower clients).
    staleTime: 0,
  });
}

// ============================================================================
// Amend Report
// ============================================================================

/**
 * Resolves to the id of the report to open next: the new amendment, the amendment of this report
 * already in progress, or the current version of a report a filed amendment superseded. The last
 * two are 409 refusals the server names a report for, so the treasurer lands on it either way. A
 * report that changed since the caller read it (a stale `concurrencyVersion`) fails with the server's
 * refresh message.
 */
export function useAmendReport() {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationFn: async ({
      reportId,
      concurrencyVersion,
    }: {
      reportId: string;
      concurrencyVersion: number;
    }): Promise<string> => {
      const response = await apiResponse(`/api/finance/reports/${reportId}/amend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ concurrencyVersion } satisfies AmendReportRequest),
      });
      if (!response.ok) {
        // Surface the server's guidance verbatim — for a portal-only jurisdiction (AL) the amend
        // endpoint returns "…make corrections in the portal", which is exactly the treasurer-facing
        // next step the amendment-first affordance shows (alert-don't-block, ADR-064). Fall back to a
        // generic message when the body isn't JSON.
        const err =
          typeof response.json === 'function' ? await response.json().catch(() => null) : null;
        if (err?.code === ErrorCode.AmendmentInProgress) return err.amendmentReportId as string;
        if (err?.code === ErrorCode.SupersededReport) return err.currentReportId as string;
        throw new Error(err?.error ?? err?.message ?? 'Failed to amend report');
      }
      return ((await response.json()) as AmendReportResult).report.id;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.all });
    },
  });

  return { amendReport: mutateAsync };
}

// ============================================================================
// Period unlock / relock (ADR-019) — the easy-but-audited manual reopen of a
// calendar-locked period. POST /api/finance/period-lock/{unlock,relock}; the
// report-detail reopen affordance sends `{ complianceReportId, reason }` (the
// backend resolves the right period key — covering deadline for in-system, the
// report itself for an externally-filed backfill). A reason is required. On
// success we invalidate the report list + the by-id detail so the lock badge /
// "reopened" banner refresh without a manual reload.
// ============================================================================

function mutatePeriodLock(
  path: 'unlock' | 'relock',
  request: PeriodLockMutationRequest,
): Promise<PeriodLockMutationResult> {
  return apiJson<PeriodLockMutationResult>(`/api/finance/period-lock/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    json: request,
    // The PeriodLockController returns the bare message string (BadRequest/NotFound),
    // so the parsed body is a string; surface it, else fall back to a generic.
    error: (body: unknown) =>
      new Error(typeof body === 'string' && body ? body : 'Failed to update the period lock.'),
  });
}

export function usePeriodLockMutation() {
  const queryClient = useQueryClient();

  // One mutation drives both directions (unlock / relock) — `isPending` is the single
  // in-flight signal the affordance reads, so it never duplicates pending state.
  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({ path, request }: { path: 'unlock' | 'relock'; request: PeriodLockMutationRequest }) =>
      mutatePeriodLock(path, request),
    onSuccess: (_result, { request }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.all });
      if (request.complianceReportId) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.finance.reports.detail(request.complianceReportId),
        });
      }
      // The lock is exactly what decides whether the close-period ledger renders its rows
      // editable, and that ledger reads the sort-inbox query (60s staleTime, no refetch on
      // focus). Without this the affordance looks like it did nothing for up to a minute.
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.sortInboxAll() });
    },
  });

  // mutateAsync is reference-stable; the wrappers are memoized so they're safe in
  // dep arrays (Golden Rule — destructure the stable mutate function).
  const unlockPeriod = useCallback(
    (request: PeriodLockMutationRequest) => mutateAsync({ path: 'unlock', request }),
    [mutateAsync],
  );
  const relockPeriod = useCallback(
    (request: PeriodLockMutationRequest) => mutateAsync({ path: 'relock', request }),
    [mutateAsync],
  );

  return { unlockPeriod, relockPeriod, isPending };
}

// USI-005 (ADR-011): FPD-015 reconciliation report retired alongside the
// backend service (deleted in USI-004). The `useReconciliationReport`
// hook + the corresponding page surface are gone.

// ============================================================================
// Obligation report generate / mark-reported — the OBLIGATION-GENERIC pair
// (GitHub #164). The `/generate` and `/mark-reported` endpoints serve both the
// AL §17-5-8.1(c) rapid report (per-contribution, contribution-flipping) AND the
// VA §24.2-947.11 pre-session report (whole-window multi-donor, supplemental,
// non-flipping — ADR-039). Server-state-driven via FilingObligation.generatedAt
// so the two-state control persists across sessions:
//   • POST /obligations/{id}/generate returns the EDI/COMET file (application/xml
//     + Content-Disposition filename) and stamps GeneratedAt → the control flips
//     to "Mark as reported".
//   • POST /obligations/{id}/mark-reported marks the obligation → Filed (the row
//     disappears). Whether it flips the contribution(s) is the obligation's
//     concern (rapid: yes; pre-session: no — `contributionFlipped: false`).
// Invalidation refreshes the obligations query (both kinds) + the pre-session
// preview (so a re-opened modal reflects the new state).
// ============================================================================

/**
 * A typed obligation-generate failure carrying the server's stable `code`
 * so the pre-session modal can translate the named refusals
 * (`PreSessionSessionDateNotConfirmed`, `PreSessionWindowStillOpen`,
 * `PreSessionNoQualifyingDonors`, plus the shared rapid codes) to plain treasurer
 * copy instead of leaking an engineer identifier. Mirrors `MarkFiledInPortalError`.
 */
export class ObligationGenerateError extends Error {
  constructor(public readonly code: string | null, message: string) {
    super(message);
    this.name = 'ObligationGenerateError';
  }
}

export interface ObligationGenerateResult {
  fileBlob: Blob;
  fileName: string;
  obligationId: string;
}

export function useGenerateObligationReport() {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationFn: async (obligationId: string): Promise<ObligationGenerateResult> => {
      const response = await authService.authenticatedFetch(
        `${API_BASE_URL}/api/finance/obligations/${obligationId}/generate`,
        { method: 'POST' },
      );
      if (!response.ok) {
        const err = await response.json().catch(() => null);
        // Preserve the stable error code so the pre-session modal can map it to
        // code-specific copy; the message is the human fallback for other callers.
        throw new ObligationGenerateError(
          (err?.code as string | undefined) ?? null,
          err?.error ?? err?.message ?? 'Failed to generate the report file',
        );
      }
      const fileBlob = await response.blob();
      // The real filename comes from Content-Disposition; the fallback only fires if
      // the server omits it (mirrors the sibling download hooks).
      const fallback = `obligation-report-${obligationId}.xml`;
      const fileName = readDispositionFileName(response.headers.get('Content-Disposition'), fallback);
      return { fileBlob, fileName, obligationId };
    },
    onSuccess: (result) => {
      // GeneratedAt is now stamped → the deck control flips to "Mark as reported".
      // Refresh the pre-session preview too so a re-opened modal isn't stale.
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.obligations() });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.finance.preSessionPreview(result.obligationId),
      });
    },
  });

  return { generateObligationReport: mutateAsync };
}

export function useMarkObligationReported() {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationFn: (obligationId: string): Promise<RapidReportMarkReportedResponse> =>
      apiJson<RapidReportMarkReportedResponse>(`/api/finance/obligations/${obligationId}/mark-reported`, {
        method: 'POST',
        error: sentenceOr('Failed to mark the report reported'),
      }),
    onSuccess: () => {
      // Obligation → Filed (drops out of the pending obligations list). A rapid
      // report also flips its contribution Confirmed → Reported; the pre-session
      // report is supplemental and flips nothing (contributionFlipped: false) — the
      // reports invalidation is harmless there. Mirrors
      // useMarkExportBatchUploaded's Reported-flip invalidations.
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.obligations() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.health() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reports.all });
    },
  });

  return { markObligationReported: mutateAsync };
}

// ============================================================================
// VA §24.2-947.11 pre-session report (GitHub #164, ADR-039) — the modal's
// preview read + the confirm-session-date attestation.
//   • GET  /obligations/{id}/pre-session-preview — the assembly plan the modal
//     renders (window, session date, blocking gaps, excluded gifts, loan alerts,
//     canGenerate). Fetched on modal open; staleTime 0 so each open is fresh.
//   • POST /obligations/{id}/confirm-session-date — writes the audited session-date
//     attestation (required before generate). Invalidates the preview because the
//     window re-scopes off the confirmed date (windowEnd = sessionStart − 1 day).
// ============================================================================

export function usePreSessionPreview(obligationId: string | null) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.preSessionPreview(obligationId ?? ''),
    queryFn: async () => {
      const response = await apiResponse(`/api/finance/obligations/${obligationId}/pre-session-preview`);
      if (response.status === 404) return null;
      if (!response.ok) throw new Error('Failed to fetch the pre-session report preview');
      return response.json() as Promise<PreSessionReportPreview>;
    },
    enabled: authService.isAuthenticated() && !!selectedCampaign && !!obligationId,
    // The counts + window must reflect the live confirmed date each open.
    staleTime: 0,
  });
}

export function useConfirmSessionDate() {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationFn: ({
      obligationId,
      sessionStartDate,
    }: {
      obligationId: string;
      // Omit to accept the computed prefill (second Wednesday of January).
      sessionStartDate?: string;
    }): Promise<PreSessionSessionDateConfirmResult> =>
      apiJson<PreSessionSessionDateConfirmResult>(
        `/api/finance/obligations/${obligationId}/confirm-session-date`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          json: sessionStartDate ? { sessionStartDate } : {},
          error: sentenceOr('Failed to confirm the session date'),
        },
      ),
    onSuccess: (result) => {
      // The window re-scopes off the confirmed date — refetch the preview so the
      // counts + windowClosed reflect the new bounds.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.finance.preSessionPreview(result.obligationId),
      });
    },
  });

  return { confirmSessionDate: mutateAsync };
}

// ============================================================================
// Daily report (§17-5-8(b)(3) daily aggregate, GitHub #81) — the daily-obligation
// strip's single "Mark filed in portal" action. POST
// /obligations/{id}/mark-filed flips the DailyAggregate obligation → Filed (the
// row disappears). Unlike the rapid report there's no Generate step — the
// treasurer files the daily report directly in the FCPA portal, then marks it
// filed here. Invalidates the obligations + health queries so the strip reflects
// the cleared obligation.
// ============================================================================

export interface DailyReportMarkFiledResult {
  obligationId: string;
  status: string;
}

export function useMarkDailyReportFiled() {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationFn: (obligationId: string): Promise<DailyReportMarkFiledResult> =>
      apiJson<DailyReportMarkFiledResult>(`/api/finance/obligations/${obligationId}/mark-filed`, {
        method: 'POST',
        error: sentenceOr('Failed to mark the daily report filed'),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.obligations() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.health() });
    },
  });

  return { markDailyReportFiled: mutateAsync };
}
